import React, { useEffect, useMemo, useState } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import EditButton from '../../../ui/EditButton';
import { isOutingPast } from '../../../../utils/outingTiming';
import { isSailingBoatBookingEnabled, isSailingOutingsEnabled } from '../../utils/moduleUtils';

function sortOutings(left, right) {
  const leftDate = left?.date?.toDate?.()?.getTime?.() ?? 0;
  const rightDate = right?.date?.toDate?.()?.getTime?.() ?? 0;
  return leftDate - rightDate;
}

export default function SailingOutingsCard({
  isAdmin,
  teamId,
  modules = {},
  onManage,
}) {
  const enabled = isSailingOutingsEnabled(modules);
  const dependenciesMet = Boolean(modules?.events?.enabled && isSailingBoatBookingEnabled(modules));
  const [outings, setOutings] = useState([]);

  useEffect(() => {
    if (!isAdmin || !enabled || !teamId) {
      setOutings([]);
      return undefined;
    }
    const unsubscribe = safeOnSnapshot(collection(db, 'teams', teamId, 'sailingOutings'), (snapshot) => {
      setOutings(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })).sort(sortOutings));
    }, undefined, { label: 'sailing-outings', teamId });
    return () => unsubscribe();
  }, [enabled, isAdmin, teamId]);

  const summary = useMemo(() => outings.reduce((acc, outing) => {
    if (String(outing.status || 'draft') === 'submitted') acc.submitted += 1;
    else acc.draft += 1;
    if (!isOutingPast(outing)) acc.upcoming += 1;
    return acc;
  }, { draft: 0, submitted: 0, upcoming: 0 }), [outings]);

  if (!isAdmin || !enabled) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">Club-led Outings</span>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => onManage?.({ createNew: true })}>New outing</button>
          <EditButton section="sailingOutings" onEdit={() => onManage?.({ managePresets: true })} />
        </div>
      </div>
      <div className="card-body">
        {!dependenciesMet ? (
          <p className="text-muted">Enable Events and Sailing Boat Booking before planning club-led outings.</p>
        ) : (
          <>
            <div className="availability-status-list">
              <div className="availability-status-item"><span>Drafts</span><span className="availability-pill unavailable">{summary.draft}</span></div>
              <div className="availability-status-item"><span>Submitted</span><span className="availability-pill available">{summary.submitted}</span></div>
              <div className="availability-status-item"><span>Upcoming</span><span className="availability-pill info">{summary.upcoming}</span></div>
            </div>
            {outings.length === 0 ? <p className="text-muted" style={{ marginTop: 12 }}>No club-led outings yet.</p> : (
              <div style={{ display: 'grid', gap: 8, marginTop: 12, maxHeight: 240, overflow: 'auto' }}>
                {outings.slice(0, 8).map((outing) => (
                  <div key={outing.id} className="list-row" style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span><strong>{outing.name || outing.linkedEventName || 'Outing'}</strong><small className="text-muted" style={{ display: 'block' }}>{String(outing.status || 'draft')}</small></span>
                    <button type="button" className="btn btn--secondary btn--xs" onClick={() => onManage?.({ outingId: outing.id })}>Open</button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
